import { Sorting } from '@devexpress/dx-react-grid'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { PackagePageData } from '@returnmates/client-core/src/graphql/custom/api'
import {
  AdminPackage,
  Package,
  PackageType,
  Trip,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getPackageTableColumnWidth,
  getPartners as getPartnersSelector,
} from '@returnmates/client-core/src/selectors/admin'
import moment from 'moment'
import momentTz from 'moment-timezone'
import { ElementType, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CONSOLIDATIONS, CONTAINERS, TRIPS_LOG } from '../../constants/paths'
import { Filter } from '../../pages/Packages'
import GridTable from '../GridTable'
import { TableColumn } from '../GridTable/types'
import Toolbar from './components/Toolbar'
import {
  DEFAULT_PACKAGE_COLUMNS,
  DEFAULT_PACKAGE_COLUMNS_WIDTH,
  formattedColumnNames,
  NOT_VISIBLE_PACKAGE_COLUMNS_ON_CONSOLIDATION_OR_CONTAINER_PAGE,
  NOT_VISIBLE_PACKAGE_COLUMNS_ON_TRIPS_PAGE,
  sortDescByName,
  tableColumnExtensions,
} from './constants'
import ProviderFormatter from './ProviderFormatter'

interface Props {
  packages: (PackagePageData | null)[]
  isNestedTable?: boolean
  showToolbar: boolean
  showDetails?: boolean
  showPaging?: boolean
  showSorting?: boolean
  canEditRow?: boolean
  canDeleteRow?: boolean
  detailsComponent?: ElementType
  editOnRowClick?: boolean
  setEditableRow?: (val: Trip | Package) => void
  onEditPackageClick?: (row: AdminPackage) => void
  onPageSizeChange?: (size: number) => void
  currentPackagePageSize?: number
  onCurrentPageChange?: (page: number) => void
  currentPageNumber?: number
  totalCount?: number
  onSortingChange?: (sort: Sorting) => void
  onFilterUpdate?: (val: Filter) => void
  setExpandedRowIds?: (val: string[]) => void
  onRemovePackage?: (id: string) => void
  expandedRowIds?: string[]
  defaultFilter?: {
    [key: string]: boolean
  }
  setSelectedRows?: (values: string[]) => void
  selectedRows?: string[]
  refreshPackages?: () => void
  canSelectRow?: boolean
  isTriplessEnabledHub?: boolean
  handleAddPackageModalOpen?: () => void
  isLoading?: boolean
}

function PackagesTable({
  packages,
  showToolbar,
  showDetails = false,
  showPaging,
  showSorting,
  canEditRow = false,
  canDeleteRow = false,
  detailsComponent: DetailsComponent,
  editOnRowClick,
  onEditPackageClick,
  onCurrentPageChange,
  currentPageNumber,
  setEditableRow,
  onPageSizeChange,
  totalCount,
  onSortingChange,
  onFilterUpdate,
  setExpandedRowIds,
  defaultFilter,
  expandedRowIds,
  onRemovePackage,
  setSelectedRows,
  selectedRows,
  refreshPackages,
  canSelectRow = true,
  isTriplessEnabledHub,
  handleAddPackageModalOpen,
  isLoading,
}: Props) {
  const [idForRemove, setIdForRemove] = useState('')
  const [isTripUpdatedLoading, setIsTripUpdateLoading] = useState(false)
  const packageTableColumnWidth = useSelector(getPackageTableColumnWidth)
  const partners = useSelector(getPartnersSelector)
  const dispatch = useDispatch()

  const columnSize = useMemo(
    () => (packageTableColumnWidth ? packageTableColumnWidth : DEFAULT_PACKAGE_COLUMNS_WIDTH),
    [packageTableColumnWidth],
  )

  const columns = useMemo((): TableColumn[] => {
    switch (window.location.pathname) {
      case TRIPS_LOG:
        return DEFAULT_PACKAGE_COLUMNS.filter(
          col => !NOT_VISIBLE_PACKAGE_COLUMNS_ON_TRIPS_PAGE.includes(col.name),
        )
      case CONTAINERS:
      case CONSOLIDATIONS:
        return DEFAULT_PACKAGE_COLUMNS.filter(
          col => !NOT_VISIBLE_PACKAGE_COLUMNS_ON_CONSOLIDATION_OR_CONTAINER_PAGE.includes(col.name),
        )
      default:
        return DEFAULT_PACKAGE_COLUMNS
    }
  }, [])

  const formatPackages = useCallback(
    (packages: (PackagePageData | null)[]) => {
      if (!packages.length) {
        return []
      }

      return packages.map((pack: PackagePageData) => {
        const lastScan = pack.scans?.[0]

        return {
          ...pack,
          partnerName:
            partners.find(partner => partner.code === pack.partnerCode)?.name || 'Returnmates',
          driver: pack.worker?.name || '',
          date: moment(pack.date).format('MM-DD-yyyy'),
          packageDate: pack.packageDate ? moment(pack.packageDate).format('MM-DD-yyyy') : null,
          expectedDate: pack.expectedDate ? moment(pack.expectedDate).format('MM-DD-yyyy') : null,
          lastScanned: lastScan?.createdAt
            ? momentTz
                .unix(lastScan.createdAt)
                .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                .format('M/D/YY h:mm A z')
            : '-',
          displayType: pack.type === PackageType.DROPOFF ? 'DELIVERY' : pack.type,
        }
      })
    },
    [partners],
  )

  const onColumnWidthsChange = useCallback(
    newTableSize => dispatch(actions.setPackageTableColumnWidth.request(newTableSize)),
    [dispatch],
  )

  useEffect(() => {
    if (idForRemove && onRemovePackage) {
      onRemovePackage(idForRemove)
      return
    }
  }, [idForRemove, onRemovePackage])

  return (
    <>
      {showToolbar ? (
        <Toolbar
          selectedRowsProp={selectedRows}
          setSelectedRowsProp={setSelectedRows}
          onFilterUpdate={onFilterUpdate}
          refreshPackages={refreshPackages}
          packagesData={formatPackages(packages)}
          defaultFilter={defaultFilter}
          isTripUpdatedLoading={isTripUpdatedLoading}
          setIsTripUpdateLoading={setIsTripUpdateLoading}
          isTriplessEnabledHub={isTriplessEnabledHub}
          handleAddPackageModalOpen={handleAddPackageModalOpen}
        />
      ) : null}

      <GridTable
        columns={columns}
        data={formatPackages(packages)}
        formattedColumnNames={formattedColumnNames}
        tableColumnExtensions={tableColumnExtensions}
        isLoading={isLoading || isTripUpdatedLoading}
        setEditableRow={setEditableRow}
        canEditRow={canEditRow}
        editOnRowClick={editOnRowClick}
        canDeleteRow={canDeleteRow}
        setIdForRemove={setIdForRemove}
        showDetails={showDetails}
        detailsComponent={DetailsComponent}
        formatterComponent={ProviderFormatter}
        detailsProps={{
          onEditPackageClick: onEditPackageClick,
        }}
        showPaging={showPaging}
        showSorting={showSorting}
        totalCount={totalCount}
        onPageSizeChange={onPageSizeChange}
        onCurrentPageChange={onCurrentPageChange}
        currentPageNumber={currentPageNumber}
        canSelectRow={canSelectRow}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        isNetworkFiltering
        onSortingChange={onSortingChange}
        expandedRowIds={expandedRowIds}
        setExpandedRowIds={setExpandedRowIds}
        defaultColumnWidths={columnSize}
        onColumnWidthsChange={onColumnWidthsChange}
        sortDescByName={sortDescByName}
        isPackageTable
      />
    </>
  )
}

export default memo(PackagesTable)
